import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Eye, EyeOff } from 'lucide-react';
import MustReadGuidelines from './MustReadGuidelines';
import PerformanceChart from './PerformanceCharts';
import ChartFilters from './ChartFilters';
import TradeStats from './TradeStats';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useBrokerIntegration } from '../../../contexts/BrokerIntegrationContextCompat';
import brokerAccountService from '../../../services/brokerAccountService';

const maskValue = (value) => {
  if (value === null || value === undefined) return '****';
  return '****';
};

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Parse YYYY-MM-DD as local calendar date (avoids UTC midnight shift). */
const parseDateInput = (value) => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const PortfolioInfo = ({ portfolioValue, marginAvailable, optionBuyingPower, isMasked }) => (
  <div className="portfolio-info mb-5 space-y-1">
    <h3 className="font-semibold text-base text-gray-200">
      Automated Portfolio value:{' '}
      <span className="text-white" data-testid="portfolio-value">
        {isMasked ? maskValue(portfolioValue) : `$${portfolioValue.toLocaleString()}`}
      </span>
    </h3>
    <h3 className="font-semibold text-base text-gray-200">
      Margin/Capital Available:{' '}
      <span className="text-white">
        {isMasked ? maskValue(marginAvailable) : `$${marginAvailable.toLocaleString()}`}
      </span>
    </h3>
    {optionBuyingPower !== undefined && optionBuyingPower !== null && (
      <h3 className="font-semibold text-base text-gray-200">
        Option Buying Power:{' '}
        <span className="text-white">
          {isMasked ? maskValue(optionBuyingPower) : `$${optionBuyingPower.toLocaleString()}`}
        </span>
      </h3>
    )}
  </div>
);

PortfolioInfo.propTypes = {
  portfolioValue: PropTypes.number.isRequired,
  marginAvailable: PropTypes.number.isRequired,
  optionBuyingPower: PropTypes.number,
  isMasked: PropTypes.bool,
};

const LeftColumn = ({ portfolioValue = 17000, marginAvailable = 35000 }) => {
  const { user } = useAuthContext();
  const { brokerIntegrations } = useBrokerIntegration();
  const [balanceData, setBalanceData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchedAccount, setLastFetchedAccount] = useState(null);
  const fetchTimeoutRef = useRef(null);

  const [isMasked, setIsMasked] = useState(() => {
    const saved = localStorage.getItem('portfolioMasked');
    // Privacy: portfolio $ values are masked by default on every load; only
    // reveal when the user has explicitly unmasked before (saved === 'false').
    return saved !== 'false';
  });

  const [selectedSymbols, setSelectedSymbols] = useState(['SPX', 'SPY', 'QQQ']);
  const [selectedPeriod, setSelectedPeriod] = useState('All');
  const [customDateRange, setCustomDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    return { startDate, endDate };
  });

  const toggleMask = useCallback(() => {
    setIsMasked((prev) => {
      const newValue = !prev;
      localStorage.setItem('portfolioMasked', String(newValue));
      return newValue;
    });
  }, []);

  const activeBroker = useMemo(() => {
    if (!Array.isArray(brokerIntegrations) || brokerIntegrations.length === 0) return null;
    return brokerIntegrations.find((i) => i?.credentials?.selectedAccountId);
  }, [brokerIntegrations]);

  const fetchBalanceData = useCallback(async () => {
    if (!user?.uid || !activeBroker) return;

    const currentAccount = `${activeBroker.broker}_${activeBroker.credentials.selectedAccountId}`;

    if (lastFetchedAccount === currentAccount) {
      return;
    }

    try {
      setIsLoading(true);
      const resp = await brokerAccountService.fetchAccountBalances({
        broker: activeBroker.broker,
        accountId: activeBroker.credentials.selectedAccountId,
        userId: user.uid,
      });

      setBalanceData(resp);
      setLastFetchedAccount(currentAccount);
    } catch (err) {
      console.error('LeftColumn balance fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid, activeBroker, lastFetchedAccount]);

  useEffect(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(() => {
      fetchBalanceData();
    }, 300);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [fetchBalanceData]);

  const displayValues = useMemo(() => {
    const calculatedPortfolioValue =
      balanceData?.data?.net_liquidating_value ??
      balanceData?.data?.total_buying_power ??
      portfolioValue;
    const calculatedMarginAvailable =
      balanceData?.data?.available_trading_funds ??
      balanceData?.data?.cash_balance ??
      marginAvailable;
    const calculatedOptionBuyingPower = balanceData?.data?.derivative_buying_power;

    return {
      portfolioValue: calculatedPortfolioValue,
      marginAvailable: calculatedMarginAvailable,
      optionBuyingPower: calculatedOptionBuyingPower,
    };
  }, [balanceData, portfolioValue, marginAvailable]);

  const {
    portfolioValue: displayPortfolioValue,
    marginAvailable: displayMarginAvailable,
    optionBuyingPower: displayOptionBuyingPower,
  } = displayValues;

  const chartCustomDateRange = selectedPeriod === 'Custom' ? customDateRange : null;

  return (
    <div className="left-column">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold text-lg text-white tracking-tight">Portfolio Overview</h3>
        <button
          onClick={toggleMask}
          data-testid="portfolio-mask-toggle"
          aria-label={isMasked ? 'Show values' : 'Hide values'}
          className="p-2 text-gray-400 hover:text-purple-400 transition-colors rounded-lg hover:bg-white/5"
          title={isMasked ? 'Show values' : 'Hide values'}
        >
          {isMasked ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>

      {isLoading && <div className="text-sm text-gray-400 mb-2">Loading balances...</div>}

      <PortfolioInfo
        portfolioValue={displayPortfolioValue}
        marginAvailable={displayMarginAvailable}
        optionBuyingPower={displayOptionBuyingPower}
        isMasked={isMasked}
      />

      <MustReadGuidelines />

      {/* Chart card — matches new Portfolio Overview design */}
      <div className="rounded-2xl border border-purple-900/40 bg-[#0b0d2a] p-4 sm:p-5 mb-6 shadow-[inset_0_1px_0_rgba(168,85,247,0.06)]">
        <ChartFilters
          symbols={selectedSymbols}
          onChangeSymbols={setSelectedSymbols}
          period={selectedPeriod}
          onChangePeriod={setSelectedPeriod}
        />

        {selectedPeriod === 'Custom' && (
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <label className="flex items-center gap-2 text-xs text-gray-400">
              From
              <input
                type="date"
                value={formatDateInput(customDateRange.startDate)}
                onChange={(e) => {
                  const next = parseDateInput(e.target.value);
                  if (next) {
                    setCustomDateRange((prev) => ({ ...prev, startDate: next }));
                  }
                }}
                className="bg-gray-800 border border-purple-700/40 rounded-lg px-2 py-1.5 text-white text-xs"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-400">
              To
              <input
                type="date"
                value={formatDateInput(customDateRange.endDate)}
                onChange={(e) => {
                  const next = parseDateInput(e.target.value);
                  if (next) {
                    setCustomDateRange((prev) => ({ ...prev, endDate: next }));
                  }
                }}
                className="bg-gray-800 border border-purple-700/40 rounded-lg px-2 py-1.5 text-white text-xs"
              />
            </label>
          </div>
        )}

        <PerformanceChart
          symbol={selectedSymbols}
          period={selectedPeriod}
          customDateRange={chartCustomDateRange}
        />

        <TradeStats />
      </div>
    </div>
  );
};

LeftColumn.propTypes = {
  portfolioValue: PropTypes.number,
  marginAvailable: PropTypes.number,
};

export default LeftColumn;
